import React, { FunctionComponent, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { IPostVehicle } from '../../../shared/services/vehicle/vehicle.model';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BuildingPeriod } from '../../../shared/interfaces/billing-period';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import moment, { Moment } from 'moment';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { FormDateInputDisplay } from '../form-field-components/new-and-edit/form-date-input';
import { FormRadioInputDisplay } from '../form-field-components/new-and-edit/form-radio-input';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model';
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils';

export const VehicleForm: FunctionComponent<{
  newVehicle: IPostVehicle,
  submitHandler: (values: IPostVehicle) => void,
  isEditPage?: boolean,
  setUpPage?: boolean,
  isSubmitted?: number
  vehicleActive?: boolean,
  errorMessage: string,
  errorCode: number | undefined,
  setErrorMessage?: React.Dispatch<React.SetStateAction<any>>,
  setErrorCode?: React.Dispatch<React.SetStateAction<any>>,
  editField?: string,
  setEditField?: React.Dispatch<React.SetStateAction<any>>,
  isSubmitting?: boolean | undefined,
}> = ({
  newVehicle,
  submitHandler,
  errorMessage,
  errorCode,
  isEditPage = false,
  vehicleActive,
  setEditField,
  editField,
  setErrorMessage,
  setErrorCode,
  isSubmitting,
}) => {
    const navigate = useNavigate()
    //Services
    const subsidiaryService = SubsidiaryService.getInstance()
    const userDetailsService = new UserDetailsService();
    const customFieldService = new CustomFieldService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const organisation = userDetailsContext.userDetails?.organisation
    const vehicleFieldConfig = organisation?.vehicleFieldConfig
    const isFieldHidden = (key: string) => vehicleFieldConfig?.[key as keyof typeof vehicleFieldConfig] === 'hidden'
    const isFieldMandatory = (key: string) => vehicleFieldConfig?.[key as keyof typeof vehicleFieldConfig] === 'mandatory'
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [users, setUsers] = React.useState<IUserDetails[]>([]);
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    // Only show custom fields not marked hidden; memoized so the reference is stable between renders
    const visibleCustomFields = useMemo(
      () => customFields?.filter(f => (f.displayMode ?? 'optional') !== 'hidden'),
      [customFields],
    )
    //Varaibles
    const ownership: string[] = [
      'Leased',
      'Owned (Using)',
      // 'Owned (Leased Out)'
    ]
    //Formik
    const vehicleSchema = Yup.object().shape({
      vinNumber: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} identification number is required`),
      customUniqueId: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} registration is required`),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      dataEntryPeriod: Yup.string().required('Frequency of data entry is required and must be either "Monthly" or "Annually"'),
      vehicleType: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} type is required and must be either "Passenger vehicle" or "Delivery vehicle"`),
      assignedUserId: isFieldMandatory('assignedUserId')
        ? Yup.string().required('Default Assignee is required')
        : Yup.string().nullable(),
      startDate: Yup.string().required('Start date is required'),
      endDate: Yup.string().nullable(),
      ownership: Yup.string().required('Ownership status is required and must be either "Leased" or "Owned (Using)"'),
      vehicleReference: isFieldMandatory('vehicleReference')
        ? Yup.string().required('Vehicle Reference is required')
        : Yup.string().nullable(),
      ...(visibleCustomFields && visibleCustomFields.length > 0 ? visibleCustomFields.reduce((acc, field) => {
        const mandatory = field.displayMode === 'mandatory' || (field.displayMode === undefined && field.isMandatory);
        acc[field.id] = mandatory
          ? Yup.string().required(`${field.fieldName} is mandatory`)
          : Yup.string().nullable();
        return acc;
      }, {} as any) : {}),
    })

    const getInitialCustomFieldValues = useCallback(() => {
      if (!visibleCustomFields || !visibleCustomFields.length) return {};

      return visibleCustomFields.reduce((acc, field) => {
        const matchingValue = isEditPage && 'customFieldValues' in newVehicle && newVehicle.customFieldValues
          ? newVehicle.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? ''
          : '';
        acc[field.id] = matchingValue;
        return acc;
      }, {} as Record<string, any>);
    }, [visibleCustomFields, isEditPage, newVehicle]);

    const formik = useFormik<any>({
      initialValues: {
        vinNumber: newVehicle.vinNumber,
        organisationId: newVehicle.organisationId,
        subsidiaryId: newVehicle.subsidiaryId,
        customUniqueId: newVehicle.customUniqueId,
        dataEntryPeriod: newVehicle.dataEntryPeriod,
        vehicleType: newVehicle.vehicleType,
        assignedUserId: newVehicle.assignedUserId,
        startDate: newVehicle.startDate,
        endDate: newVehicle.endDate,
        ownership: newVehicle.ownership,
        vehicleReference: newVehicle.vehicleReference ? newVehicle.vehicleReference.split(',').join(', ') : null,
        ...getInitialCustomFieldValues(),
      },
      validationSchema: vehicleSchema,
      onSubmit: (values) => {
        const customFieldValues = Array.isArray(visibleCustomFields)
          ? visibleCustomFields.map((field) => ({
            customFieldId: field.id,
            value: values?.[field.id] ?? null,
          }))
          : []
        submitHandler({
          ...values,
          customFieldValues,
        })
      }
    });

    const formikRef = useRef(formik);
    formikRef.current = formik;

    //UseEffects
    useEffect(() => {
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => console.log(err))

      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })

      customFieldService.getAllActive(`&entryType=vehicle`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    useEffect(() => {
      if (!newVehicle) return;

      const customFieldValues = getInitialCustomFieldValues();
      const preferredSubsidiary = subsidiaries ? getPreferredSubsidiary(subsidiaries, userDetailsContext.userDetails) : undefined;
      const resolvedSubsidiaryId = isEditPage ? newVehicle.subsidiaryId : preferredSubsidiary?.id;

      formikRef.current.setValues({
        vinNumber: newVehicle.vinNumber,
        organisationId: newVehicle.organisationId,
        subsidiaryId: resolvedSubsidiaryId,
        customUniqueId: newVehicle.customUniqueId,
        dataEntryPeriod: newVehicle.dataEntryPeriod,
        vehicleType: newVehicle.vehicleType,
        assignedUserId: newVehicle.assignedUserId,
        startDate: newVehicle.startDate,
        endDate: newVehicle.endDate,
        ownership: newVehicle.ownership,
        vehicleReference: newVehicle.vehicleReference ? newVehicle.vehicleReference.split(',').join(', ') : null,
        ...customFieldValues,
      });
    }, [customFields, newVehicle, isEditPage, getInitialCustomFieldValues, subsidiaries, userDetailsContext.userDetails])

    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (newVehicle) {
        const originalCustomFieldValues = getInitialCustomFieldValues();

        formik.setValues({
          vinNumber: newVehicle.vinNumber,
          organisationId: newVehicle.organisationId,
          subsidiaryId: newVehicle.subsidiaryId,
          customUniqueId: newVehicle.customUniqueId,
          dataEntryPeriod: newVehicle.dataEntryPeriod,
          vehicleType: newVehicle.vehicleType,
          assignedUserId: newVehicle.assignedUserId,
          startDate: newVehicle.startDate,
          endDate: newVehicle.endDate,
          ownership: newVehicle.ownership,
          vehicleReference: newVehicle.vehicleReference ? newVehicle.vehicleReference.split(',').join(', ') : null,
          ...originalCustomFieldValues,
        })
      }
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date && name === 'startDate') {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    return (
      <Box component='form'
        onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isTopRow={true}
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'vinNumber'}
          title={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Identification Number`}
          name='vinNumber'
          value={formik.values.vinNumber}
          touched={formik.touched.vinNumber}
          errors={formik.errors.vinNumber}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!vehicleActive}
          customErrorMessage={errorCode === 1 ? errorMessage : ''}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'customUniqueId'}
          title={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Registration`}
          name='customUniqueId'
          value={formik.values.customUniqueId}
          touched={formik.touched.customUniqueId}
          errors={formik.errors.customUniqueId}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!vehicleActive}
          customErrorMessage={errorCode === 2 ? errorMessage : ''}
        />
        {!isFieldHidden('vehicleReference') && (
          <FormTextInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'vehicleReference'}
            title={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Reference`}
            name='vehicleReference'
            value={formik.values.vehicleReference}
            touched={formik.touched.vehicleReference}
            errors={formik.errors.vehicleReference}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!vehicleActive}
            tooltip='Enter multiple references separated by commas (e.g., REF1, REF2, REF3)'
          />
        )}
        {((subsidiaries && subsidiaries.length > 1) || (subsidiaries && organisation?.enableSubsidiaries)) &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'subsidiaryId'}
            title={`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
            name='subsidiaryId'
            value={formik.values.subsidiaryId}
            touched={formik.touched.subsidiaryId}
            errors={formik.errors.subsidiaryId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!vehicleActive}
            array={getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
            customErrorMessage={errorCode === 7 ? errorMessage : ''}
          />}
        <FormDateInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'startDate'}
          title='Start Date'
          name='startDate'
          value={moment(formik.values.startDate)}
          touched={formik.touched.startDate}
          errors={formik.errors.startDate}
          onChange={(date: any) => dateHandler(date, 'startDate')}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!vehicleActive}
          customErrorMessage={errorCode === 9 ? errorMessage : undefined}
        />
        {isEditPage && !vehicleActive &&
          <FormDateInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'endDate'}
            title='End Date'
            name='endDate'
            value={moment(formik.values.endDate)}
            touched={formik.touched.endDate}
            errors={formik.errors.endDate}
            onChange={(date: any) => dateHandler(date, 'endDate')}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!vehicleActive}
            allowEdit={false}
          />}
        {ownership &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            allowEdit={false}
            disabled={isEditPage && editField !== 'ownership'}
            title='Ownership'
            name='ownership'
            value={formik.values.ownership}
            touched={formik.touched.ownership}
            errors={formik.errors.ownership}
            onChange={formik.handleChange}
            customErrorMessage={errorCode === 6 ? errorMessage : ''}
            array={ownership}
          />}
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={(isEditPage && editField !== 'dataEntryPeriod')}
          title={'Frequency of Data Entry'}
          name='dataEntryPeriod'
          value={formik.values.dataEntryPeriod}
          touched={formik.touched.dataEntryPeriod}
          errors={formik.errors.dataEntryPeriod}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!vehicleActive}
          array={Object.values(BuildingPeriod)}
          customErrorMessage={errorCode === 8 ? errorMessage : ''}
        />
        <FormRadioInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={(isEditPage && editField !== 'vehicleType')}
          title={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Type`}
          name='vehicleType'
          value={formik.values.vehicleType}
          touched={formik.touched.vehicleType}
          errors={formik.errors.vehicleType}
          onChange={formik.handleChange}
          optionOneLabel='Passenger vehicle'
          optionOneValue='Passenger vehicle'
          optionTwoValue='Delivery vehicle'
          optionTwoLabel='Delivery vehicle'
          cancelEditHandler={cancelEditHandler}
          hideEditIcon={!vehicleActive}
          customErrorMessage={errorCode === 4 ? errorMessage : ''}
        />
        {!isFieldHidden('assignedUserId') && (
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== 'assignedUserId'}
            title='Default Assignee'
            name='assignedUserId'
            value={formik.values.assignedUserId}
            touched={formik.touched.assignedUserId}
            errors={formik.errors.assignedUserId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!vehicleActive}
            array={users.sort((a, b) => a.firstName.localeCompare(b.firstName))}
            valueKey={'id'}
            labelKey={(user: any) => `${user.firstName} ${user.surname}`}
            isBottomRow={(!visibleCustomFields || visibleCustomFields.length === 0) && !(isEditPage && newVehicle.conversionId)}
          />
        )}
        {visibleCustomFields && visibleCustomFields.length > 0 && visibleCustomFields.map((field, index) => (
          <FormTextInputDisplay
            key={field.id}
            isBottomRow={index === visibleCustomFields.length - 1 && !(isEditPage && newVehicle.conversionId)}
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            disabled={isEditPage && editField !== field.id}
            title={field.fieldName}
            name={field.id}
            type={field.fieldType === 'number' ? 'number' as const : 'text' as const}
            value={formik.values[field.id]}
            touched={formik.touched[field.id]}
            errors={formik.errors[field.id]}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            hideEditIcon={!vehicleActive}
          />
        ))
        }
        {isEditPage && newVehicle.conversionId && (
          <FormTextInputDisplay
            isBottomRow={true}
            isEditPage={isEditPage}
            disabled={true}
            allowEdit={false}
            title='Default Conversion Factor'
            name='conversionFactor'
            value={[
              newVehicle.conversionSource?.source,
              newVehicle.conversion?.detailLevelOne,
              newVehicle.conversion?.detailLevelTwo,
              newVehicle.conversion?.detailLevelThree,
            ].filter(Boolean).join(' – ')}
            onChange={() => { }}
          />
        )}
        {
          !isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate('/organisation/vehicles')}
              submitHandler={formik.submitForm}
            />
          </Stack>
        }
      </Box >
    )
  }